import { useMemo, useRef, useState } from "react";
import type { MouseEvent as ReactMouseEvent } from "react";
import { COLORS } from "../../../../../utils/colors";
import type { ChartPoint } from "../../types";
import {
  ChartEmpty,
  ChartFrame,
  ChartSvg,
  ChartTooltip,
  ChartTooltipDate,
  ChartTooltipValue,
} from "../../style";

interface AreaChartProps {
  data: ChartPoint[];
  strokeColor: string;
  fillColor: string;
  valueColor: string;
  valueLabel: string;
  gradientId: string;
  defaultIndex: number;
}

const VB_W = 620;
const VB_H = 260;
const MARGIN = { top: 16, right: 20, bottom: 34, left: 44 };
const PLOT_W = VB_W - MARGIN.left - MARGIN.right;
const PLOT_H = VB_H - MARGIN.top - MARGIN.bottom;
const BASE_Y = MARGIN.top + PLOT_H;
const MAX_X_LABELS = 7;

interface Pt {
  x: number;
  y: number;
}

function niceNum(value: number): number {
  const exponent = Math.floor(Math.log10(value));
  const fraction = value / 10 ** exponent;
  const niceFraction =
    fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 5 ? 5 : 10;
  return niceFraction * 10 ** exponent;
}

// Round the data max up to a clean value whose quarters are whole numbers.
function niceMax(rawMax: number): number {
  if (rawMax <= 4) return 4;
  return niceNum(Math.ceil(rawMax / 4)) * 4;
}

function xFor(index: number, count: number): number {
  if (count <= 1) return MARGIN.left + PLOT_W / 2;
  return MARGIN.left + (index / (count - 1)) * PLOT_W;
}

function smoothLine(pts: Pt[]): string {
  if (pts.length === 0) return "";
  if (pts.length === 1) return `M ${pts[0].x} ${pts[0].y}`;
  let d = `M ${pts[0].x} ${pts[0].y}`;
  for (let i = 0; i < pts.length - 1; i += 1) {
    const p0 = pts[i - 1] ?? pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] ?? p2;
    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
  }
  return d;
}

export default function AreaChart({
  data,
  strokeColor,
  fillColor,
  valueColor,
  valueLabel,
  gradientId,
  defaultIndex,
}: AreaChartProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  const yMax = useMemo(
    () => niceMax(Math.max(...data.map((p) => p.value), 0)),
    [data],
  );
  const yTicks = useMemo(
    () => [yMax, (yMax * 3) / 4, yMax / 2, yMax / 4, 0],
    [yMax],
  );

  const yFor = useMemo(
    () => (value: number) => MARGIN.top + (1 - value / yMax) * PLOT_H,
    [yMax],
  );

  const points = useMemo<Pt[]>(
    () => data.map((p, i) => ({ x: xFor(i, data.length), y: yFor(p.value) })),
    [data, yFor],
  );

  const linePath = useMemo(() => smoothLine(points), [points]);
  const areaPath =
    points.length > 0
      ? `${linePath} L ${points[points.length - 1].x} ${BASE_Y} L ${points[0].x} ${BASE_Y} Z`
      : "";

  // Show at most MAX_X_LABELS evenly-spaced labels (first + last always
  // included). Evenly spacing — rather than "every Nth + force last" — avoids
  // the last label colliding with its neighbour on long ranges.
  const lastIndex = data.length - 1;
  const labelIndices = new Set<number>();
  if (data.length <= MAX_X_LABELS) {
    for (let i = 0; i < data.length; i += 1) labelIndices.add(i);
  } else {
    for (let k = 0; k < MAX_X_LABELS; k += 1) {
      labelIndices.add(Math.round((k * lastIndex) / (MAX_X_LABELS - 1)));
    }
  }

  if (data.length === 0) {
    return (
      <ChartEmpty align="center" justify="center">
        No data for this period
      </ChartEmpty>
    );
  }

  const activeIndex = Math.min(
    Math.max(hoverIndex ?? defaultIndex, 0),
    data.length - 1,
  );
  const active = points[activeIndex];
  const activeData = data[activeIndex];

  function handleMove(event: ReactMouseEvent<SVGSVGElement>) {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * VB_W;
    let nearest = 0;
    let best = Infinity;
    points.forEach((p, i) => {
      const dist = Math.abs(p.x - x);
      if (dist < best) {
        best = dist;
        nearest = i;
      }
    });
    setHoverIndex(nearest);
  }

  return (
    <ChartFrame>
      <ChartSvg
        ref={svgRef}
        viewBox={`0 0 ${VB_W} ${VB_H}`}
        preserveAspectRatio="xMidYMid meet"
        onMouseMove={handleMove}
        onMouseLeave={() => setHoverIndex(null)}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={fillColor} />
            <stop offset="100%" stopColor="rgba(255, 255, 255, 0)" />
          </linearGradient>
        </defs>

        {yTicks.map((tick) => {
          const y = yFor(tick);
          return (
            <g key={tick}>
              <line
                x1={MARGIN.left}
                y1={y}
                x2={VB_W - MARGIN.right}
                y2={y}
                stroke={COLORS.chartGrid}
                strokeWidth={1}
              />
              <text
                x={MARGIN.left - 12}
                y={y + 4}
                textAnchor="end"
                fontSize={11}
                fill={COLORS.chartAxis}
              >
                {tick}
              </text>
            </g>
          );
        })}

        <path d={areaPath} fill={`url(#${gradientId})`} stroke="none" />
        <path
          d={linePath}
          fill="none"
          stroke={strokeColor}
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {data.map((p, i) =>
          labelIndices.has(i) ? (
            <text
              key={`${p.label}-${i}`}
              x={xFor(i, data.length)}
              y={VB_H - 10}
              textAnchor={i === 0 ? "start" : i === lastIndex ? "end" : "middle"}
              fontSize={11}
              fill={COLORS.chartAxis}
            >
              {p.label}
            </text>
          ) : null,
        )}

        {active && (
          <>
            <line
              x1={active.x}
              y1={MARGIN.top}
              x2={active.x}
              y2={BASE_Y}
              stroke={COLORS.chartCrosshair}
              strokeWidth={1}
            />
            <circle
              cx={active.x}
              cy={active.y}
              r={6}
              fill={COLORS.chartMarkerFill}
              stroke={strokeColor}
              strokeWidth={3}
            />
          </>
        )}
      </ChartSvg>

      {active && activeData && (
        <ChartTooltip
          $left={(active.x / VB_W) * 100}
          $top={(active.y / VB_H) * 100}
          $align={
            active.x / VB_W >= 0.82
              ? "right"
              : active.x / VB_W <= 0.18
                ? "left"
                : "center"
          }
        >
          <ChartTooltipDate>{activeData.label}</ChartTooltipDate>
          <ChartTooltipValue $color={valueColor}>
            {valueLabel} : {activeData.value}
          </ChartTooltipValue>
        </ChartTooltip>
      )}
    </ChartFrame>
  );
}
